import { max } from "@suse-o11y/max";
import { msPerFrame } from "@suse-o11y/ms-per-frame";
import { round } from "@suse-o11y/round";
import { Signal } from "@suse-o11y/signal";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";

import { useTimestampCursor } from "../TimestampCursor";

import { TimelineContextProvider, type TimelineContextValue, type TimelineProviderProps } from "./_context";

const PX_REFRESH_RATE = 1 / 8; // means that every pixel will be updated 8 times per second

export const TimelineProvider: React.FC<React.PropsWithChildren<TimelineProviderProps>> = ({
	frozen,
	stageWidth,
	timestamp,
	timeRange,
	eventBucketsState,
	lineState,
	formatTimestamp,
	children,
}) => {
	const timestampCursor = useTimestampCursor();
	const frozenTimeRange = usePermanent(() => Signal(timeRange.toSpecific()));

	const value = React.useMemo<TimelineContextValue>(() => {
		const refreshInterval = max(msPerFrame(60), round((timeRange.duration() / stageWidth) * PX_REFRESH_RATE, 0));

		return {
			frozen,
			timestamp,
			timeRange,
			frozenTimeRange,
			eventBucketsState,
			lineState,
			timestampCursor,
			refreshInterval,
			stageWidth,
			formatTimestamp,
		};
	}, [
		frozen,
		timestamp,
		timeRange,
		frozenTimeRange,
		eventBucketsState,
		lineState,
		timestampCursor,
		stageWidth,
		formatTimestamp,
	]);

	return <TimelineContextProvider value={value}>{children}</TimelineContextProvider>;
};
